<template>
  <div
    id="conditionalParent"
    class="mx-4 my-6 flex h-full flex-col justify-between overflow-y-auto"
  >
    <div v-if="selectedOutcome?.checks?.length" class="flex flex-col">
      <template
        v-for="(section, index) in selectedOutcome?.checks"
        :key="index"
      >
        <conditional-argument-list
          :current-orchestration-step="currentOrchestrationStep"
          :section="section"
          :section-index="Number(index)"
          @save="handleAttributeSave"
        />
        <div
          v-if="index !== selectedOutcome.checks.length - 1"
          class="c-connector ml-4"
        >
          <span
            class="w-max rounded-full px-2 py-0.5 text-xs font-semibold text-gray-400 uppercase"
          >
            {{ $t("COMMON.OR") }}
          </span>
        </div>
      </template>
      <add-conditions-group-button
        v-if="!isActiveTemplatePublished"
        :step="currentOrchestrationStep"
        @click="handleAddConditionsGroup"
      />
      <auto-populate-opposite-condition-button
        v-if="showAutoPopulateOppositeConditionButton"
        :step="currentOrchestrationStep"
        :selected-condition-types-and-values="selectedConditionTypesAndValues"
        :active-condition-outcome="activeConditional?.outcome"
      />
    </div>
    <conditional-argument-empty
      v-else
      :step="currentOrchestrationStep"
      @save="handleAttributeSave"
    />
  </div>
  <webhook-configuration
    v-if="activeConditional?.stepId"
    :step-id="activeConditional.stepId"
    :outcome="activeConditional?.outcome"
    :webhooks="activeConditional.webhooks"
  />
  <modal-buttons
    v-if="!isActiveTemplatePublished"
    :on-delete="handleDelete"
    class="bg-disabled pb-6"
    :white-background="false"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import { AnalyticsEvent } from "@/enums/analytics";
import { ValidOppositeConditions } from "@/enums/orchestration";
import {
  OPPOSITE_CONDITIONS,
  VALID_OPPOSITE_TYPES,
} from "@/helpers/constants/orchestration";
import {
  useAttributesOptions,
  useDataOrchestration,
  useDoActiveTemplateStatus,
} from "@/hooks/orchestration";
import { useAnalyticsStore } from "@/stores/analytics";

import AddConditionsGroupButton from "./AddConditionsGroupButton.vue";
import ConditionalArgumentEmpty from "./ConditionalArgumentEmpty.vue";
import ConditionalArgumentList from "./ConditionalArgumentList.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import WebhookConfiguration from "@/views/profile/components/dataOrchestration/WebhookConfiguration.vue";
import AutoPopulateOppositeConditionButton from "@/views/profile/components/dataOrchestration/configPanel/AutoPopulateOppositeConditionButton.vue";

import type { ConditionType } from "@/models/common";
import type {
  DataOrchestrationOutcome,
  IActiveConfigPanelConditional,
  IStep,
  IStepAttribute,
} from "@/models/orchestration";

const { getters, commit } = useStore();
const { isActiveTemplatePublished } = useDoActiveTemplateStatus();
const { activeTemplate } = useDataOrchestration();
const { attributeOptions } = useAttributesOptions();
const { trackEvent } = useAnalyticsStore();

const activeConditional = computed<IActiveConfigPanelConditional | null>(
  () => getters["orchestration/activeConfigPanelConditional"]
);

const currentOrchestrationStepOutcomesTitles = computed(
  () =>
    currentOrchestrationStep.value?.outcomes.map(({ outcome }) => outcome) || []
);

const selectedConditionTypesAndValues = computed(() =>
  currentOrchestrationStep.value?.outcomes.flatMap(({ checks }) =>
    (checks || []).flatMap(({ conditions }) =>
      conditions.flatMap(({ conditions }) => conditions)
    )
  )
);

const showAutoPopulateOppositeConditionButton = computed(() => {
  if (isActiveTemplatePublished.value) {
    return false;
  }

  const validConditions = [
    ValidOppositeConditions.PASS,
    ValidOppositeConditions.FAIL,
  ];

  if (
    !validConditions.includes(
      selectedOutcome.value?.outcome as ValidOppositeConditions
    )
  ) {
    return false;
  }

  const hasPassAndFailSet = (
    validConditions as DataOrchestrationOutcome[]
  ).every((outcome) =>
    currentOrchestrationStepOutcomesTitles.value.includes(outcome)
  );

  if (hasPassAndFailSet) {
    return false;
  }

  if (!selectedConditionTypesAndValues.value?.length) {
    return false;
  }

  const availableAttributeOptionsConditions = attributeOptions.value
    .filter(({ id }) =>
      currentOrchestrationStep.value?.attributes
        ?.map((attribute) => attribute.id)
        .includes(id)
    )
    .map(({ meta, id }) => ({ conditions: meta.conditions, id }));

  if (!hasOppositeConditionsAvailable(availableAttributeOptionsConditions)) {
    return false;
  }

  return selectedConditionTypesAndValues.value
    .flatMap(({ type }) => type)
    .every((type) => VALID_OPPOSITE_TYPES.includes(type));
});

const currentOrchestrationStep = computed(() => {
  if (!activeConditional.value?.stepId) {
    return null;
  }
  const steps = getters["orchestration/activeTemplateSteps"] as IStep[];
  const step = steps.find((s) => s.id === activeConditional.value?.stepId);
  return step || null;
});

const selectedOutcome = computed(() =>
  currentOrchestrationStep.value?.outcomes?.find(
    (o) => o.outcome === activeConditional.value?.outcome
  )
);

const hasOppositeConditionsAvailable = (
  availableAttributeConditions: {
    conditions: ConditionType[];
    id: string;
  }[]
) => {
  const neededOutcomes = currentOrchestrationStep.value?.outcomes.filter(
    ({ outcome }) =>
      [ValidOppositeConditions.PASS, ValidOppositeConditions.FAIL].includes(
        outcome as ValidOppositeConditions
      )
  );

  if (!neededOutcomes?.length) {
    return false;
  }

  return neededOutcomes.every(({ checks }) =>
    checks?.every(({ conditions }) =>
      conditions.every(({ attribute, conditions: innerConditions }) => {
        const availableConditions = availableAttributeConditions.find(
          (item) => item.id === attribute
        )?.conditions;

        if (!availableConditions?.length) {
          return false;
        }

        return innerConditions.every(
          ({ type }) =>
            type === "boolean" ||
            availableConditions.includes(
              OPPOSITE_CONDITIONS[type] as ConditionType
            )
        );
      })
    )
  );
};

const handleAttributeSave = (
  attributes: IStepAttribute[],
  checkId: number | null = null
) => {
  if (!currentOrchestrationStep.value?.id || !selectedOutcome.value?.outcome) {
    return;
  }
  commit("orchestration/appendNextCheckCondition", {
    stepId: currentOrchestrationStep.value.id,
    checkId,
    outcome: selectedOutcome.value?.outcome,
    attributes,
  });
  trackEvent(AnalyticsEvent.DataOrchestrationConditionAdded, {
    template_id: activeTemplate.value?.id,
    step_id: currentOrchestrationStep.value.id,
    outcome: selectedOutcome.value?.outcome,
    attributes_count: attributes.length,
  });
};

const handleAddConditionsGroup = () => {
  commit("orchestration/addConditionsGroup", {
    stepId: currentOrchestrationStep.value?.id,
    outcome: selectedOutcome.value?.outcome,
  });
};

const handleDelete = () => {
  if (!currentOrchestrationStep.value?.id || !selectedOutcome.value?.outcome) {
    return;
  }

  const stepId = currentOrchestrationStep.value.id;
  const outcomeValue = selectedOutcome.value.outcome;

  // Only primary outcomes (not followers) should trigger step deletion
  if (
    !selectedOutcome.value.follows_other_outcome_path &&
    selectedOutcome.value.next_step
  ) {
    const allSteps: IStep[] = getters["orchestration/activeTemplateSteps"];

    const otherOutcomesPointingToSameStep = allSteps.some((step) =>
      step.outcomes.some(
        (outcome) =>
          outcome.next_step === selectedOutcome.value?.next_step &&
          !(step.id === stepId && outcome.outcome === outcomeValue)
      )
    );

    if (!otherOutcomesPointingToSameStep) {
      commit("orchestration/deleteStep", {
        stepId: selectedOutcome.value.next_step,
      });
    }
  }

  commit("orchestration/deleteConditional", {
    stepId,
    outcome: outcomeValue,
  });
  trackEvent(AnalyticsEvent.DataOrchestrationConditionDeleted, {
    template_id: getters["orchestration/activeTemplate"]?.id,
    step_id: stepId,
    outcome: outcomeValue,
  });

  commit("orchestration/setActiveConfigPanel", "");
  commit("orchestration/setActiveConfigPanelConditional", null);
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.c-connector::before,
.c-connector::after {
  content: "";
  height: 12px;
  width: 3px;
  background-color: rgba(55, 105, 214, 0.15);
  display: block;
  transform: translate(13px, 0);
}

.c-connector span {
  background-color: rgba(55, 105, 214, 0.15);
}
</style>
